import { useState, type KeyboardEvent } from 'react';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  type SelectChangeEvent,
} from '@mui/material';

import type { NodeItem } from '@models/flows';

const CONDITION_TYPES = [
  { value: 'userLocale', label: 'User Locale' },
  { value: 'userMailDomain', label: 'User Mail Domain' },
  { value: 'originPath', label: 'Origin Path' },
  { value: 'issuerName', label: 'Issuer Name' },
  { value: 'issuerCountry', label: 'Issuer Country' },
  { value: 'paymentMethodType', label: 'Payment Method Type' },
  { value: 'everythingElse', label: 'Everything Else' },
] as const;

type ConditionalNodeConfigProps = {
  currentNode: NodeItem;
  onChange: (data: NodeItem) => void;
};

function ConditionalNodeConfiguration({ currentNode, onChange }: ConditionalNodeConfigProps) {
  const [inputValue, setInputValue] = useState('');

  if (!currentNode) return null;

  const values = Array.isArray(currentNode.value) ? (currentNode.value as string[]) : [];

  const isCatchAll = currentNode.code === 'everythingElse';

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    onChange({
      ...currentNode,
      code: event.target.value,
      label: CONDITION_TYPES.find((t) => t.value === event.target.value)?.label || '',
      value: currentNode.value,
    });
  };

  const handleAddValue = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || values.includes(trimmed)) return;

    onChange({
      ...currentNode,
      value: [...values, trimmed],
    });
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddValue();
    }
  };

  const handleRemoveValue = (valueToRemove: string) => {
    onChange({
      ...currentNode,
      value: values.filter((v) => v !== valueToRemove),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <FormControl fullWidth>
        <InputLabel id="condition-type-label">Condition Type</InputLabel>
        <Select
          labelId="condition-type-label"
          value={currentNode.code || ''}
          label="Condition Type"
          onChange={handleTypeChange}
          MenuProps={{ disablePortal: true }}>
          {CONDITION_TYPES.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!isCatchAll && (
        <>
          <FormControl fullWidth>
            <TextField
              label="Add value (press Enter)"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </FormControl>
          {values.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {values.map((val) => (
                <Chip key={val} label={val} size="small" onDelete={() => handleRemoveValue(val)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ConditionalNodeConfiguration;
